import React, { FC, useEffect, useState } from 'react';
import get from 'lodash/get';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import TimelineTemplateInfo from '../TimelineTemplateInfo';
import EventTitle from 'components/events/Timeline/EventTitle';
import TimelineLayout from 'components/layouts/TimelineLayout';
import PageContentHeader from 'components/common/PageContentHeader';
import SectionBlocks from 'components/sections/blocks/SectionBlocks';
import TemplateSections from 'components/events/Timeline/TemplateSections';
import { SectionsFilterableContext } from 'components/events/Timeline/SectionsFilterContext';
import { TimelineContext, TypeTimelineContext } from 'components/events/Timeline/TimelineContext';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { TEMPLATE_TIMELINE_GQL } from './constants';
import { useEvent } from 'components/context/EventContext';
import { TEMPLATE_PAGE_TAB_KEYS } from 'services/constants';
import { updateSectionIdFromRoute } from 'services/sections/helpers';

import { TEMPLATE_SECTIONS, TEMPLATE_SECTIONS_NO_IMAGES_ALLOWED } from 'graphql/queries/template';
import { UPDATE_TEMPLATE_SECTION } from 'graphql/mutations/template';

import { Modals } from 'types/enums';
import { RouteParams, TemplateTimelineGqlOptions } from './interfaces';
import { SectionSettingsModalProps } from 'components/modals/SectionSettingsModal';

const TemplateTimeline: FC = () => {
  const [selectedSection, setSelectedSection] = useState<Nullable<TemplateSection>>(null);
  const [filter, setFilter] = useState<Nullable<SectionsFilter>>(null);

  const eventContext = useEvent();
  const { event } = eventContext;

  const navigate = useNavigate();
  const location = useLocation();

  const { id = '', sectionId = '', page } = useParams<keyof RouteParams>();

  const templateId: string = id;
  const baseTimelineRoute: string = `/template/${templateId}/${TEMPLATE_PAGE_TAB_KEYS.timeline}`;

  const { openModal } = useModal();

  const openSectionSettinsModal = (section: TimelineSection) =>
    openModal<SectionSettingsModalProps<TimelineSection>>({
      key: Modals.sectionSettings,
      props: {
        section,
        updateSectionMutation: UPDATE_TEMPLATE_SECTION,
        variables: {
          templateId,
          sectionId: section._id,
        },
        refetchQueries: ['templateSections'],
        eventContext,
      },
    });

  const { data, loading, refetch } = useQuery<TemplateSectionsResponse, TemplateSectionsVariables>(
    eventContext.isCoverPhotoAllowed ? TEMPLATE_SECTIONS : TEMPLATE_SECTIONS_NO_IMAGES_ALLOWED,
    {
      variables: { templateId, ...(!!filter?.q ? { filter } : {}) },
      fetchPolicy: 'cache-and-network',
      skip: eventContext.isLoading,
      onError,
    }
  );
  const sections: TemplateSection[] = get(data, 'templateSections', []);

  const shouldRenderComponentsForSection: boolean = !!selectedSection && !!sections.length;

  const config: TypeTimelineContext<TemplateTimelineGqlOptions> = {
    variables: {
      templateId,
      sectionId,
    },
    settings: {
      canAnswer: false,
    },
    ...TEMPLATE_TIMELINE_GQL,
  };

  useEffect(() => {
    updateSectionIdFromRoute(selectedSection, setSelectedSection, sectionId, sections);
  }, [sections, sectionId]);

  useEffect(() => {
    if (selectedSection && selectedSection._id !== sectionId) {
      navigate({
        pathname: `${baseTimelineRoute}/${selectedSection._id}`,
        search: location.search,
      });
    }
  }, [selectedSection, page, location.search]);

  return !!event ? (
    <TimelineContext.Provider value={config}>
      <TimelineTemplateInfo />
      <TimelineLayout
        left={
          <SectionsFilterableContext.Provider value={{ filter, setFilter }}>
            <TemplateSections
              template={(event as unknown) as Template}
              sections={sections}
              selectedSection={selectedSection}
              onSectionClick={setSelectedSection}
              onEditClick={(section: TimelineSection) => {
                openSectionSettinsModal(section);
              }}
              refetch={refetch}
              isSectionsLoading={loading}
            />
          </SectionsFilterableContext.Provider>
        }
        right={
          shouldRenderComponentsForSection &&
          selectedSection && (
            <>
              <SectionBlocks<TemplateSection>
                sections={sections}
                section={selectedSection}
                onEditClick={() => {
                  openSectionSettinsModal(selectedSection);
                }}
              />
            </>
          )
        }
      >
        <PageContentHeader>
          <EventTitle name={event.title} />
        </PageContentHeader>
      </TimelineLayout>
    </TimelineContext.Provider>
  ) : null;
};

export default TemplateTimeline;
